import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";
import { SectionHeader } from "../components/shared";
import { Input } from "../components/ui/input";

const GROUP_ORDER = ["Customer", "Vendor", "Item"];
const groupKey = (t) => (t.startsWith("Sales") ? "Sales Documents" : t.startsWith("Purchase") ? "Purchase Documents" : t);

export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);

  const { data, isLoading } = useQuery({
    queryKey: ["search-full", q],
    queryFn: async () => (await api.get(`/search?q=${encodeURIComponent(q)}&full=1`)).data,
    enabled: q.trim().length >= 2,
  });
  const results = data?.results || [];

  const groups = {};
  for (const r of results) {
    const k = groupKey(r.type);
    (groups[k] = groups[k] || []).push(r);
  }
  const orderedKeys = [
    ...GROUP_ORDER.filter((k) => groups[k]),
    ...Object.keys(groups).filter((k) => !GROUP_ORDER.includes(k)),
  ];

  const submit = (e) => {
    e.preventDefault();
    if (input.trim().length >= 2) setSearchParams({ q: input.trim() }, { replace: true });
  };

  return (
    <div className="animate-fade-up max-w-4xl">
      <SectionHeader title="Search results" subtitle={q ? `Matches for “${q}”` : "Search across everything"} />

      <form onSubmit={submit} className="relative mb-6 max-w-xl" data-testid="search-page-form">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="search-page-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search customers, vendors, items, documents…"
          className="pl-10 h-11 rounded-sm"
          autoFocus
        />
      </form>

      {q.trim().length < 2 && <p className="text-muted-foreground text-sm">Type at least 2 characters to search.</p>}
      {q.trim().length >= 2 && isLoading && <p className="text-muted-foreground text-sm">Searching…</p>}
      {q.trim().length >= 2 && !isLoading && results.length === 0 && (
        <div className="border border-border bg-card text-center py-16 text-muted-foreground" data-testid="search-page-empty">
          <MagnifyingGlass size={36} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No matches for “{q}”.</p>
        </div>
      )}

      {!isLoading && results.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground mb-4" data-testid="search-page-count">{results.length} result{results.length === 1 ? "" : "s"}</p>
          <div className="space-y-7">
            {orderedKeys.map((k) => (
              <div key={k} data-testid="search-group">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="overline-label">{k}</h3>
                  <span className="text-xs text-muted-foreground">({groups[k].length})</span>
                </div>
                <div className="border border-border bg-card divide-y divide-border">
                  {groups[k].map((r, i) => (
                    <button
                      key={i}
                      data-testid="search-page-result"
                      onClick={() => navigate(r.to)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-secondary transition-colors group"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{r.label}</p>
                        {r.sub && <p className="text-xs text-muted-foreground truncate">{r.sub}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-[10px] uppercase font-bold text-muted-foreground border border-border px-1.5 py-0.5 rounded-sm">{r.type}</span>
                        <ArrowRight size={15} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
